/**
 * Authentication requests.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.13
 */
import type { AuthResponse, LoginRequest, SessionUser } from '@nexus/contracts';

import { api, setAccessToken } from '../../../lib/api-client';

/**
 * Signs in.
 *
 * The access token is stored in memory here rather than by the caller, so no
 * component has to remember to do it. The refresh token arrives as an httpOnly
 * cookie and is never visible to JavaScript (ADR 0004).
 */
export async function login(credentials: LoginRequest): Promise<SessionUser> {
  const response = await api.post<AuthResponse>('/auth/login', credentials);
  setAccessToken(response.accessToken);
  return response.user;
}

/**
 * Ends the session.
 *
 * The token is cleared even when the request fails: a server that cannot be
 * reached must not leave the user apparently signed in, and the refresh cookie
 * expires on its own.
 */
export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } finally {
    setAccessToken(null);
  }
}

/**
 * Restores a session from the refresh cookie.
 *
 * Called once on load. A 401 here is the normal "not signed in" case, not an
 * error worth surfacing, so the caller treats a rejection as an absent session.
 */
export async function restoreSession(): Promise<SessionUser> {
  const response = await api.post<AuthResponse>('/auth/refresh');
  setAccessToken(response.accessToken);
  return response.user;
}

/** Fetches the current caller. */
export async function fetchCurrentUser(): Promise<SessionUser> {
  return api.get<SessionUser>('/auth/me');
}
